import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  Divider as MuiDivider,
  MenuItem,
  InputAdornment,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  createTheme,
  ThemeProvider,
  CssBaseline
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import TerminalIcon from '@mui/icons-material/Terminal';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { GoogleLogin } from '@react-oauth/google';
import Drawer from '@mui/material/Drawer';

const ALLOWED_DOMAIN = 'quintype.com';

function decodeGoogleJwt(credential) {
  try {
    const payload = credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pathfinder_user')); } catch { return null; }
  });
  const [authError, setAuthError] = useState('');

  const handleGoogleSuccess = (response) => {
    const payload = decodeGoogleJwt(response.credential);
    if (!payload) { setAuthError('Login failed — could not read token.'); return; }
    if (!payload.email?.endsWith(`@${ALLOWED_DOMAIN}`)) {
      setAuthError(`Access restricted to @${ALLOWED_DOMAIN} accounts. Got: ${payload.email}`);
      return;
    }
    const userData = { email: payload.email, name: payload.name, picture: payload.picture };
    localStorage.setItem('pathfinder_user', JSON.stringify(userData));
    setUser(userData);
    setAuthError('');
  };

  const handleLogout = () => {
    localStorage.removeItem('pathfinder_user');
    setUser(null);
  };

  const [historyOpen, setHistoryOpen] = useState(false);

  const HISTORY_KEY = `pathfinder_history_${user?.email || 'anon'}`;
  const MAX_HISTORY = 20;

  const loadHistory = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []; } catch { return []; }
  }, [HISTORY_KEY]);

  const saveToHistory = useCallback((inputUrls, resultList) => {
    if (!resultList.length) return;
    const domains = [...new Set(inputUrls.map(u => { try { return new URL(u).hostname; } catch { return null; } }).filter(Boolean))];
    const entry = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      domains,
      urlCount: inputUrls.length,
      matchCount: resultList.filter(r => r.type !== 'Unknown' && r.type !== 'Error').length,
      urls: inputUrls,
      results: resultList,
    };
    const prev = loadHistory();
    const updated = [entry, ...prev].slice(0, MAX_HISTORY);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(updated)); } catch {}
  }, [HISTORY_KEY, loadHistory]);

  const deleteHistoryEntry = (id) => {
    const updated = loadHistory().filter(e => e.id !== id);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    setHistoryOpen(h => !h); setHistoryOpen(h => !h); // force re-render
  };

  const restoreHistoryEntry = (entry) => {
    setUrls(entry.urls);
    setRawUrlText(entry.urls.join('\n'));
    setResults(entry.results);
    setCloudflareGroups(buildCloudflareGroups(entry.results));
    setHistoryOpen(false);
  };

  const [urls, setUrls] = useState([]);
  const [rawUrlText, setRawUrlText] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterConfidence, setFilterConfidence] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [processLog, setProcessLog] = useState([]);
  const [cloudflareGroups, setCloudflareGroups] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const logContainerRef = useRef(null);
  const processLogSectionRef = useRef(null);
  const resultsSectionRef = useRef(null);
  const cloudflareSectionRef = useRef(null);
  const activeRequestControllerRef = useRef(null);
  const stopRequestedRef = useRef(false);

  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
    setProcessLog(prev => {
      const next = [...prev, { timestamp, message, type }];
      // Keep only the latest entries to avoid excessive re-renders for very large URL lists.
      return next.length > 500 ? next.slice(-500) : next;
    });
  }, []);

  // Auto-scroll log to bottom on new entries
  useEffect(() => {
    // Scroll only inside the log panel; avoid page-level scroll/focus jumps.
    if (loading && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [processLog, loading]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToSection = useCallback((sectionRef) => {
    if (!sectionRef?.current) return;
    const stickyHeaderOffset = 108;
    const targetY = sectionRef.current.getBoundingClientRect().top + window.scrollY - stickyHeaderOffset;
    window.scrollTo({ top: Math.max(targetY, 0), behavior: 'smooth' });
  }, []);

  const stopProcessing = useCallback((message = 'Processing stopped by user.') => {
    stopRequestedRef.current = true;
    if (activeRequestControllerRef.current) {
      activeRequestControllerRef.current.abort();
    }
    setLoading(false);
    addLog(message, 'warn');
  }, [addLog]);

  const handleApiError = useCallback((err) => {
    if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
      return 'canceled';
    }
    if (err?.response?.status === 422) {
      stopProcessing('CF rate-limited (422). Stopping process.');
      setError('CF rate-limited');
      return 'rate-limited';
    }
    return 'other';
  }, [stopProcessing]);

  // Pre-compiled regex patterns for better performance
  const URL_PATTERNS = useRef({
    newlines: /\r?\n|\r/g,
    hrefTag: /href=["']{1,2}([^"'>]+)["']{1,2}/i,
    trailingSlash: /\/+$/,
    doubleSlash: /\/\//,
    protocol: /^https?:\/\//,
    htmlTag: /<\/?a[^>]*>/gi,
    firstUrlToken: /https?:\/\/[^\s"'<>]+/i
  }).current;

  // Caches for performance
  const urlCacheRef = useRef(new Map());
  const configCacheRef = useRef(new Map());

  const COMMON_PATTERNS = [
    {
      name: 'Remove /ml from start',
      transform: urlObj => {
        if (urlObj.pathname.startsWith('/ml/')) {
          urlObj.pathname = urlObj.pathname.replace(/^\/ml\//, '/');
          return urlObj.toString();
        }
        return null;
      }
    },
    {
      name: 'Remove /en from start',
      transform: urlObj => {
        if (urlObj.pathname.startsWith('/en/')) {
          urlObj.pathname = urlObj.pathname.replace(/^\/en\//, '/');
          return urlObj.toString();
        }
        return null;
      }
    },
    {
      name: 'Remove trailing slash',
      transform: urlObj => {
        if (urlObj.pathname.length > 1 && urlObj.pathname.endsWith('/')) {
          urlObj.pathname = urlObj.pathname.replace(/\/+$/, '');
          return urlObj.toString();
        }
        return null;
      }
    },
    {
      name: 'Replace double slashes',
      transform: urlObj => {
        // Only suggest if double slashes are present and not due to merged URLs
        const path = urlObj.pathname;
        // Check for 'http' or 'https' in the path (not at the start)
        if (/\/\//.test(path) && !/(http|https):\/.+/.test(path)) {
          urlObj.pathname = path.replace(/\/\//g, '/');
          return urlObj.toString();
        }
        return null;
      }
    }
  ];

  // Function to clean and normalize URLs with caching
  // Detect if a URL contains an embedded full URL in its path (e.g. domain/https://other-domain/...)
  const extractEmbeddedUrl = useCallback((url) => {
    const match = url.match(/^https?:\/\/[^/]+\/(https?:\/\/.+)$/);
    return match ? match[1] : null;
  }, []);

  const cleanUrl = useCallback((url) => {
    if (urlCacheRef.current.has(url)) {
      return urlCacheRef.current.get(url);
    }

    try {
      let cleaned = url.replace(URL_PATTERNS.newlines, ' ').trim();

      if (cleaned.includes('href="')) {
        const domainMatch = cleaned.match(/^(https?:\/\/[^<\s]+)/);
        const domain = domainMatch ? domainMatch[1] : '';
        const hrefMatch = cleaned.match(URL_PATTERNS.hrefTag);
        if (hrefMatch && hrefMatch[1]) {
          let href = hrefMatch[1].replace(/^"+|"+$/g, '');
          cleaned = (href.startsWith('/') && domain) ? domain + href : href;
        }
      }
      
      cleaned = cleaned.replace(URL_PATTERNS.htmlTag, '').replace(/^"|"$/g, '');

      // If the input has additional trailing text (e.g. date strings), keep only the first URL token.
      const firstUrlMatch = cleaned.match(URL_PATTERNS.firstUrlToken);
      if (firstUrlMatch && firstUrlMatch[0]) {
        cleaned = firstUrlMatch[0];
      }

      if (!cleaned.startsWith('http')) {
        cleaned = `${cleaned.startsWith('/') ? '' : '/'}${cleaned}`;
      }

      if (!cleaned.includes('?') && cleaned.includes('&')) {
        const lastSlashIdx = cleaned.lastIndexOf('/');
        const firstAmpIdx = cleaned.indexOf('&', lastSlashIdx);
        if (firstAmpIdx !== -1) {
          cleaned = cleaned.substring(0, firstAmpIdx) + '?' + cleaned.substring(firstAmpIdx + 1);
        }
      }

      const urlObj = new URL(cleaned);
      let pathname = urlObj.pathname.replace(URL_PATTERNS.trailingSlash, '').replace(/([^:]\/)\/+/g, '$1');
      const cleanParams = new URLSearchParams();
      for (const [key, value] of urlObj.searchParams.entries()) {
        if (key && value && !key.includes('&') && !value.includes('&')) {
          cleanParams.append(key, value);
        }
      }
      urlObj.pathname = pathname;
      urlObj.search = cleanParams.toString();
      const result = urlObj.toString();
      urlCacheRef.current.set(url, result);
      return result;
    } catch (e) {
      const fallback = url.replace(/\s+/g, '').replace(/\/+/g, '/').replace(/&+/g, '&').replace(/\?+/g, '?').replace(/\/$/, '');
      urlCacheRef.current.set(url, fallback);
      return fallback;
    }
  }, [URL_PATTERNS]);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    Papa.parse(file, {
      complete: (results) => {
        const urls = results.data.flat()
          .filter(url => url)
          .map(url => cleanUrl(url));
        setUrls(urls);
      },
      error: (error) => {
        setError('Error parsing CSV file: ' + error.message);
      }
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.txt']
    }
  });

  // Decode a URL path segment, handling double-encoded sequences (e.g. %25E0... → %E0... → Tamil)
  const fullDecode = (s) => {
    let decoded = s;
    try { decoded = decodeURIComponent(s); } catch { return s; }
    if (decoded.includes('%')) {
      try { decoded = decodeURIComponent(decoded); } catch {}
    }
    return decoded;
  };

  // Helper functions for proxy API calls
  const PROXY_BASE = '/api/proxy';
  const API_TIMEOUT_MS = 3000;
  const getApiConfig = () => ({
    timeout: API_TIMEOUT_MS,
    signal: activeRequestControllerRef.current?.signal
  });
  const fetchStoryBySlug = async (domain, slug) => {
    return axios.post(`${PROXY_BASE}/story`, { domain, slug }, getApiConfig());
  };
  const fetchTagBySlug = async (domain, slug) => {
    return axios.post(`${PROXY_BASE}/tag`, { domain, slug }, getApiConfig());
  };
  const fetchConfig = async (domain) => {
    return axios.post(`${PROXY_BASE}/config`, { domain }, getApiConfig());
  };
  const fetchStoryByExternalId = async (domain, externalId) => {
    return axios.post(`${PROXY_BASE}/story-by-external-id`, { domain, externalId }, getApiConfig());
  };
  const fetchAuthorBySlug = async (domain, slug) => {
    return axios.post(`${PROXY_BASE}/author`, { domain, slug }, getApiConfig());
  };
  const fetchMetabaseMatches = async (domain, slugs, externalIds = []) => {
    return axios.post(`${PROXY_BASE}/metabase`, { domain, slugs, externalIds }, { ...getApiConfig(), timeout: 15000 });
  };

  // Add delay helper
  const delay = ms => new Promise(res => setTimeout(res, ms));
  
  // Get config with caching
  const getConfigForDomain = useCallback(async (domain) => {
    if (configCacheRef.current.has(domain)) {
      return { data: configCacheRef.current.get(domain), timedOut: false, rateLimited: false };
    }
    try {
      const config = await fetchConfig(domain);
      configCacheRef.current.set(domain, config.data);
      return { data: config.data, timedOut: false, rateLimited: false };
    } catch (e) {
      return {
        data: null,
        timedOut: e.code === 'ECONNABORTED' || e.message?.includes('timeout'),
        rateLimited: e?.response?.status === 422
      };
    }
  }, []);
  
  // Get unique domains from URLs
  const getUniqueDomains = useCallback((urlList) => {
    const domains = new Set();
    urlList.forEach(url => {
      try {
        const urlObj = new URL(url);
        domains.add(urlObj.origin);
      } catch (e) {}
    });
    return Array.from(domains);
  }, []);

  const buildCloudflareGroups = useCallback((resultList) => {
    const eligible = resultList.filter((r) =>
      r &&
      !['Unknown', 'Error'].includes(r.type) &&
      typeof r.originalUrl === 'string' &&
      typeof r.redirectUrl === 'string' &&
      r.originalUrl.startsWith('http') &&
      r.redirectUrl.startsWith('http')
    );

    const parsedRows = eligible.map((row, index) => {
      try {
        const sourceUrl = new URL(row.originalUrl);
        const targetUrl = new URL(row.redirectUrl);
        const sourceOriginNormalized = `https://${sourceUrl.host.toLowerCase()}`;
        const targetOriginNormalized = `https://${targetUrl.host.toLowerCase()}`;
        const sourceParts = sourceUrl.pathname.split('/').filter(Boolean);
        const targetParts = targetUrl.pathname.split('/').filter(Boolean);

        const sourceHead = sourceParts[0] || '';
        const sourceSecond = sourceParts[1] || '';
        const targetHead = targetParts[0] || '';

        const sourcePatternHead = sourceHead
          ? `${sourceOriginNormalized}/${sourceHead}${sourceParts.length > 1 ? '/*' : ''}`
          : `${sourceOriginNormalized}/`;

        const sourcePatternHeadSecond = sourceHead && sourceSecond
          ? `${sourceOriginNormalized}/${sourceHead}/${sourceSecond}${sourceParts.length > 2 ? '/*' : ''}`
          : sourcePatternHead;

        let targetPattern;
        if (targetParts.length <= 1 || targetUrl.pathname.endsWith('.php')) {
          targetPattern = `${targetOriginNormalized}${targetUrl.pathname.replace(/\/$/, '') || '/'}`;
        } else {
          targetPattern = `${targetOriginNormalized}/${targetHead}/*`;
        }

        return {
          index,
          row,
          sourcePatternHead,
          sourcePatternHeadSecond,
          targetPattern,
          sourceSpecificity: sourcePatternHeadSecond.length
        };
      } catch (e) {
        return null;
      }
    }).filter(Boolean);

    const sourceStats = new Map();
    for (const parsed of parsedRows) {
      const sourceCandidates = [parsed.sourcePatternHead, parsed.sourcePatternHeadSecond];
      const uniqueCandidates = Array.from(new Set(sourceCandidates));

      for (const sourcePattern of uniqueCandidates) {
        if (!sourceStats.has(sourcePattern)) {
          sourceStats.set(sourcePattern, {
            total: 0,
            targets: new Map(),
            specificity: sourcePattern.length
          });
        }

        const sourceEntry = sourceStats.get(sourcePattern);
        sourceEntry.total += 1;

        if (!sourceEntry.targets.has(parsed.targetPattern)) {
          sourceEntry.targets.set(parsed.targetPattern, {
            count: 0,
            rowIndexes: [],
            typeCounts: new Map()
          });
        }

        const targetEntry = sourceEntry.targets.get(parsed.targetPattern);
        targetEntry.count += 1;
        targetEntry.rowIndexes.push(parsed.index);
        targetEntry.typeCounts.set(parsed.row.type, (targetEntry.typeCounts.get(parsed.row.type) || 0) + 1);
      }
    }

    const candidates = [];
    for (const [sourcePattern, stats] of sourceStats.entries()) {
      if (stats.total < 2) continue;

      let bestTargetPattern = null;
      let bestTargetData = null;
      for (const [targetPattern, targetData] of stats.targets.entries()) {
        if (!bestTargetData || targetData.count > bestTargetData.count) {
          bestTargetPattern = targetPattern;
          bestTargetData = targetData;
        }
      }

      if (!bestTargetData || bestTargetData.count < 2) continue;

      const purity = bestTargetData.count / stats.total;
      if (purity < 0.6) continue;

      let dominantType = 'Mixed';
      let maxTypeCount = 0;
      for (const [type, count] of bestTargetData.typeCounts.entries()) {
        if (count > maxTypeCount) {
          maxTypeCount = count;
          dominantType = type;
        }
      }

      candidates.push({
        sourcePattern,
        targetPattern: bestTargetPattern,
        count: bestTargetData.count,
        purity,
        rowIndexes: bestTargetData.rowIndexes,
        dominantType,
        specificity: stats.specificity
      });
    }

    candidates.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      if (b.purity !== a.purity) return b.purity - a.purity;
      return b.specificity - a.specificity;
    });

    const usedRows = new Set();
    const groups = [];

    for (const candidate of candidates) {
      const freeRowIndexes = candidate.rowIndexes.filter((idx) => !usedRows.has(idx));
      if (freeRowIndexes.length < 2) continue;

      for (const idx of freeRowIndexes) {
        usedRows.add(idx);
      }

      const sourcePathSegments = freeRowIndexes.map((idx) => {
        try {
          return new URL(parsedRows[idx].row.originalUrl).pathname.split('/').filter(Boolean);
        } catch (e) {
          return [];
        }
      });

      let safeExpression = '';
      if (sourcePathSegments.length > 0) {
        const firstSegment = sourcePathSegments[0][0] || '';
        const sameFirstSegment = firstSegment && sourcePathSegments.every((parts) => parts[0] === firstSegment);
        const twoSegments = sourcePathSegments.every((parts) => parts.length === 2);
        const numericSecondSegment = sourcePathSegments.every((parts) => /^\d+$/.test(parts[1] || ''));

        if (sameFirstSegment && twoSegments && numericSecondSegment) {
          const escapedPrefix = firstSegment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          try {
            const sampleUrl = new URL(parsedRows[freeRowIndexes[0]].row.originalUrl);
            safeExpression = `(http.host eq "${sampleUrl.host}" and http.request.uri.path matches "^/${escapedPrefix}/\\d+$")`;
          } catch (e) {
            safeExpression = '';
          }
        }
      }

      const confidence = candidate.purity >= 0.85 ? 'High' : candidate.purity >= 0.7 ? 'Medium' : 'Low';
      groups.push({
        groupType: candidate.dominantType,
        sourcePattern: candidate.sourcePattern,
        targetPattern: candidate.targetPattern,
        count: freeRowIndexes.length,
        confidence,
        safeExpression,
        examples: freeRowIndexes.slice(0, 3).map((idx) => ({
          original: parsedRows[idx].row.originalUrl,
          redirect: parsedRows[idx].row.redirectUrl
        }))
      });
    }

    return groups;
  }, []);



  const processUrls = async () => {
    // Parse raw text input into urls before processing
    const parsedFromText = rawUrlText.split('\n').map(u => u.trim()).filter(Boolean).map(u => cleanUrl(u));
    const activeUrls = urls.length > 0 ? urls : parsedFromText;
    if (activeUrls.length === 0) {
      setError('Please upload URLs or enter them manually');
      return;
    }
    if (parsedFromText.length > 0 && urls.length === 0) setUrls(parsedFromText);

    setLoading(true);
    setError('');
    setResults([]);
    setProcessLog([]);
    stopRequestedRef.current = false;
    activeRequestControllerRef.current = new AbortController();
    const failedResults = [];
    let newResults = [];

    addLog(`Starting analysis of ${activeUrls.length} URL${activeUrls.length !== 1 ? 's' : ''}`, 'info');

    // Pre-fetch configs for all domains
    const uniqueDomains = getUniqueDomains(activeUrls);
    addLog(`Found ${uniqueDomains.length} unique domain${uniqueDomains.length !== 1 ? 's' : ''}: ${uniqueDomains.join(', ')}`, 'info');
    const domainConfigs = new Map();
    const configAvailability = new Map();
    for (const domain of uniqueDomains) {
      if (stopRequestedRef.current) break;
      addLog(`Fetching site config for ${domain}... (3s timeout)`, 'info');
      const result = await getConfigForDomain(domain);
      if (result.data) {
        domainConfigs.set(domain, result.data);
        configAvailability.set(domain, true);
        addLog(`✓ Config loaded for ${domain} (${result.data.sections?.length || 0} sections)`, 'success');
      } else if (result.rateLimited) {
        configAvailability.set(domain, false);
        stopProcessing('CF rate-limited (422). Stopping process.');
        setError('CF rate-limited');
        break;
      } else if (result.timedOut) {
        configAvailability.set(domain, false);
        addLog(`✗ Config fetch timed out for ${domain} (>3s) — section matching skipped`, 'error');
      } else {
        configAvailability.set(domain, false);
        addLog(`✗ Failed to load config for ${domain}`, 'error');
      }
    }

    // Pre-fetch metabase matches for all URLs (bulk DB lookup before individual API calls)
    addLog('─────────────────────────────────────────', 'info');
    addLog('Running metabase bulk lookup...', 'info');
    const storyHitMap = new Map();      // `${domain}:${slug}` -> { redirectPath }
    const tagHitMap = new Map();         // `${domain}:${slug}` -> { redirectPath }
    const externalIdHitMap = new Map();  // `${domain}:${externalId}` -> { redirectPath }
    for (const domain of uniqueDomains) {
      if (stopRequestedRef.current) break;
      const domainUrls = activeUrls.filter(url => {
        try { return new URL(cleanUrl(url)).origin === domain; } catch { return false; }
      });
      const allCandidates = new Set();
      const allExternalIdCandidates = new Set();
      for (const url of domainUrls) {
        try {
          const pathParts = new URL(cleanUrl(url)).pathname.split('/').filter(Boolean);
          if (pathParts.length > 0) {
            const lastRaw = pathParts[pathParts.length - 1];
            // If last segment is numeric, treat as external_id and also check prev segment as slug
            if (/^\d+$/.test(lastRaw)) {
              allExternalIdCandidates.add(lastRaw);
              if (pathParts.length >= 2) {
                const prevRaw = pathParts[pathParts.length - 2];
                allCandidates.add(fullDecode(prevRaw));
              }
            } else {
              allCandidates.add(fullDecode(lastRaw));
              // Also check numeric suffix after last hyphen as external_id
              const hyphenParts = lastRaw.split('-');
              const possibleId = hyphenParts[hyphenParts.length - 1];
              if (hyphenParts.length > 1 && /^\d+$/.test(possibleId)) {
                allExternalIdCandidates.add(possibleId);
              }
            }
          }
        } catch {}
      }
      if (allCandidates.size === 0 && allExternalIdCandidates.size === 0) continue;
      addLog(`  → Querying metabase: ${allCandidates.size} slug(s), ${allExternalIdCandidates.size} external_id(s) for ${domain}`, 'info');
      try {
        const resp = await fetchMetabaseMatches(domain, Array.from(allCandidates), Array.from(allExternalIdCandidates));
        const matches = resp.data?.matches || [];
        for (const match of matches) {
          if (match.type === 'tag') {
            tagHitMap.set(`${domain}:${match.slug}`, { redirectPath: `topic/${match.slug}` });
          } else if (match.type === 'story-by-external-id') {
            externalIdHitMap.set(`${domain}:${match.externalId}`, { redirectPath: match.canonicalSlug });
          } else {
            storyHitMap.set(`${domain}:${match.slug}`, { redirectPath: match.canonicalSlug || match.slug });
          }
        }
        addLog(`  ✓ Metabase: ${matches.length} match${matches.length !== 1 ? 'es' : ''} found for ${domain}`, 'success');
      } catch (e) {
        const apiErrorType = handleApiError(e);
        if (apiErrorType === 'rate-limited' || apiErrorType === 'canceled') break;
        addLog(`  ⚠ Metabase pre-fetch failed for ${domain} — falling back to API calls`, 'warn');
      }
    }
    addLog('─────────────────────────────────────────', 'info');

    const BATCH_SIZE = 10;
    for (let i = 0; i < activeUrls.length; i += BATCH_SIZE) {
      if (stopRequestedRef.current) break;
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      const totalBatches = Math.ceil(activeUrls.length / BATCH_SIZE);
      addLog(`Processing batch ${batchNum}/${totalBatches} (URLs ${i + 1}–${Math.min(i + BATCH_SIZE, activeUrls.length)})`, 'info');
      const batch = activeUrls.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(async (url, batchOffset) => {
        const inputIndex = i + batchOffset;
        try {
          if (stopRequestedRef.current) return null;
          const cleanedUrl = cleanUrl(url);

          // Detect embedded URL pattern: domain/https://other-domain/...
          const embeddedUrl = extractEmbeddedUrl(cleanedUrl);
          if (embeddedUrl) {
            addLog(`⚠ Embedded URL detected: ${cleanedUrl.substring(0, 60)}...`, 'warn');
            return {
              inputIndex,
              originalUrl: url,
              cleanedUrl: cleanedUrl,
              type: 'External Link Error',
              redirectUrl: embeddedUrl,
              confidence: 'High'
            };
          }

          const urlObj = new URL(cleanedUrl);
          const domain = urlObj.origin;
          const pathParts = urlObj.pathname.split('/').filter(Boolean);
          let result = {
            inputIndex,
            originalUrl: url,
            cleanedUrl: cleanedUrl,
            type: 'Unknown',
            redirectUrl: 'No match found',
            confidence: 'Low'
          };
          let matched = false;
          let rateLimitHit = false;

          const rawLastSegment = pathParts[pathParts.length - 1];
          const lastSegment = fullDecode(rawLastSegment);

          const isNumericLast = /^\d+$/.test(lastSegment);

          // Determine external_id candidate: purely numeric lastSegment, or numeric hyphen suffix
          let externalIdCandidate = null;
          if (isNumericLast) {
            externalIdCandidate = lastSegment;
          } else {
            const hyphenParts = lastSegment.split('-');
            const possibleId = hyphenParts[hyphenParts.length - 1];
            if (hyphenParts.length > 1 && /^\d+$/.test(possibleId)) {
              externalIdCandidate = possibleId;
            }
          }
          const externalIdKey = externalIdCandidate ? `${domain}:${externalIdCandidate}` : null;

          // For slug/tag lookups: if last segment is numeric, use the segment before it
          // (falls back to treating the number as a page number if no external_id match)
          let lookupSegment = lastSegment;
          if (isNumericLast && pathParts.length >= 2) {
            lookupSegment = fullDecode(pathParts[pathParts.length - 2]);
          }
          const storyKey = `${domain}:${lookupSegment}`;
          const tagKey = `${domain}:${lookupSegment}`;

          // If the URL contains "category", section check runs first
          const doSectionFirst = pathParts.some(p => /categor/i.test(p));

          if (!doSectionFirst) {
            // 1. Metabase external_id check (runs first when last segment is numeric)
            if (externalIdKey && externalIdHitMap.has(externalIdKey)) {
              const { redirectPath } = externalIdHitMap.get(externalIdKey);
              addLog(`  ✓ Metabase match — Story by external_id "${externalIdCandidate}" (${redirectPath})`, 'success');
              return { ...result, type: 'Story', redirectUrl: `${domain}/${redirectPath}`, confidence: 'High' };
            }
            if (isNumericLast) {
              addLog(`  ℹ No external_id match for "${lastSegment}" — treating as page number, using "${lookupSegment}" for slug lookup`, 'info');
            }
            // 2. Metabase story check
            if (lookupSegment && storyHitMap.has(storyKey)) {
              const { redirectPath } = storyHitMap.get(storyKey);
              addLog(`  ✓ Metabase match — Story (${redirectPath})`, 'success');
              return { ...result, type: 'Story', redirectUrl: `${domain}/${redirectPath}`, confidence: 'High' };
            }
            // 3. Metabase tag check
            if (lookupSegment && tagHitMap.has(tagKey)) {
              const { redirectPath } = tagHitMap.get(tagKey);
              addLog(`  ✓ Metabase match — Tag (${redirectPath})`, 'success');
              return { ...result, type: 'Tag', redirectUrl: `${domain}/${redirectPath}`, confidence: 'High' };
            }
          }

          // 3. Section check from config
          if (pathParts.length > 0) {
            // Special case: /section/{name}
            if (pathParts[0] === 'section' && pathParts.length > 1) {
              const sectionName = decodeURIComponent(pathParts[1]);
              const slugified = sectionName.toLowerCase().replace(/\s+/g, '-');
              try {
                const config = domainConfigs.get(domain);
                const sections = config?.sections || [];
                const matchingSection = sections.find(section => {
                  const sectionUrl = section['section-url'] || '';
                  const sectionSlug = section.slug || '';
                  const lastPart = sectionUrl.split('/').filter(Boolean).pop();
                  return lastPart === slugified || sectionSlug === slugified;
                });
                if (matchingSection) {
                  let redirectUrl = matchingSection['section-url'];
                  if (!redirectUrl.startsWith('http')) {
                    redirectUrl = `${domain}${redirectUrl}`;
                  }
                  result = { ...result, type: 'Section', redirectUrl, confidence: 'High' };
                  matched = true;
                }
              } catch (e) {}
            }
            // Section: check all path segments from last to first
            if (!matched) {
              try {
                const config = domainConfigs.get(domain);
                const sections = config?.sections || [];
                for (let i = pathParts.length - 1; i >= 0 && !matched; i--) {
                  let sectionSlug = pathParts[i];
                  let matchingSection = sections.find(section =>
                    section['section-url'] && (section['section-url'].endsWith('/' + sectionSlug) || section['section-url'] === '/' + sectionSlug)
                  );
                  // If not found, try singular (remove trailing 's')
                  if (!matchingSection && sectionSlug.endsWith('s')) {
                    const singular = sectionSlug.slice(0, -1);
                    matchingSection = sections.find(section => 
                      section['section-url'] && (section['section-url'].endsWith('/' + singular) || section['section-url'] === '/' + singular)
                    );
                  }
                  // Optionally: handle 'ies' -> 'y'
                  if (!matchingSection && sectionSlug.endsWith('ies')) {
                    const singularY = sectionSlug.slice(0, -3) + 'y';
                    matchingSection = sections.find(section => 
                      section['section-url'] && (section['section-url'].endsWith('/' + singularY) || section['section-url'] === '/' + singularY)
                    );
                  }
                  // If still not found, try slugified version (lowercase, spaces to hyphens)
                  if (!matchingSection && /[A-Z ]/.test(sectionSlug)) {
                    const slugified = sectionSlug.toLowerCase().replace(/\s+/g, '-');
                    matchingSection = sections.find(section => 
                      section['section-url'] && (section['section-url'].endsWith('/' + slugified) || section['section-url'] === '/' + slugified)
                    );
                  }
                  if (matchingSection) {
                    let redirectUrl = matchingSection['section-url'];
                    if (!redirectUrl.startsWith('http')) {
                      redirectUrl = `${domain}${redirectUrl}`;
                    }
                    result = {
                      ...result,
                      type: 'Section',
                      redirectUrl,
                      confidence: 'High'
                    };
                    matched = true;
                    break;
                  }
                }
              } catch (e) {}
            }
          }

          // For category URLs: fall through to metabase after section miss
          if (!matched && doSectionFirst) {
            if (externalIdKey && externalIdHitMap.has(externalIdKey)) {
              const { redirectPath } = externalIdHitMap.get(externalIdKey);
              addLog(`  ✓ Metabase match — Story by external_id "${externalIdCandidate}" (${redirectPath})`, 'success');
              return { ...result, type: 'Story', redirectUrl: `${domain}/${redirectPath}`, confidence: 'High' };
            }
            if (isNumericLast) {
              addLog(`  ℹ No external_id match for "${lastSegment}" — treating as page number, using "${lookupSegment}" for slug lookup`, 'info');
            }
            if (lookupSegment && storyHitMap.has(storyKey)) {
              const { redirectPath } = storyHitMap.get(storyKey);
              addLog(`  ✓ Metabase match — Story (${redirectPath})`, 'success');
              return { ...result, type: 'Story', redirectUrl: `${domain}/${redirectPath}`, confidence: 'High' };
            }
            if (lookupSegment && tagHitMap.has(tagKey)) {
              const { redirectPath } = tagHitMap.get(tagKey);
              addLog(`  ✓ Metabase match — Tag (${redirectPath})`, 'success');
              return { ...result, type: 'Tag', redirectUrl: `${domain}/${redirectPath}`, confidence: 'High' };
            }
          }

          // API fallback: story slug (all trailing combinations, then individual segments)
          // DISABLED
          // API fallback: story by external ID (numeric suffix after last hyphen)
          // DISABLED
          // API fallback: tag
          // DISABLED
          // API fallback: author
          // DISABLED

          if (!matched) {
            addLog(`  ✗ No match found for ${urlObj.pathname}`, 'warn');
            failedResults.push({ url, cleanedUrl });
          }

          if (rateLimitHit) {
            setError(prev => prev ? prev + '\nRate limit hit (429 Too Many Requests). Please wait and try again.' : 'Rate limit hit (429 Too Many Requests). Please wait and try again.');
          }

          return result;
        } catch (error) {
          addLog(`  ✗ Error processing URL: ${url.substring(0, 60)}`, 'error');
          return {
            inputIndex,
            originalUrl: url,
            cleanedUrl: 'Invalid URL',
            type: 'Error',
            redirectUrl: 'Error processing URL',
            confidence: 'Low'
          };
        }
      });
      const batchResults = (await Promise.all(batchPromises)).filter(Boolean);
      if (stopRequestedRef.current) break;
      newResults = [...newResults, ...batchResults];
      setResults(prev => [...prev, ...batchResults]);
      // Minimal delay to allow UI updates
      if (i + BATCH_SIZE < activeUrls.length) {
        await delay(50);
      }
    }

    const matched = newResults.filter(r => r.type !== 'Unknown' && r.type !== 'Error').length;
    addLog(`─────────────────────────────────────────`, 'info');
    addLog(
      stopRequestedRef.current
        ? `Stopped. ${matched}/${newResults.length} URLs matched before stop.`
        : `Done. ${matched}/${newResults.length} URLs matched successfully.`,
      matched === newResults.length && !stopRequestedRef.current ? 'success' : 'info'
    );
    setLoading(false);
    activeRequestControllerRef.current = null;

    // Pattern discovery with early exit and caching
    const patternMap = {};
    for (const fail of failedResults) {
      try {
        const urlObj = new URL(fail.cleanedUrl);
        for (const pattern of COMMON_PATTERNS) {
          const transformed = pattern.transform(urlObj);
          if (transformed) {
            if (!patternMap[pattern.name]) patternMap[pattern.name] = [];
            patternMap[pattern.name].push({ original: fail.cleanedUrl, suggestion: transformed });
          }
        }
      } catch {}
    }
    const suggestionList = Object.entries(patternMap).map(([pattern, urls]) => ({
      pattern,
      count: urls.length,
      examples: urls.slice(0, 3),
    }));
    setSuggestions(suggestionList);
    const generatedGroups = buildCloudflareGroups(newResults);
    setCloudflareGroups(generatedGroups);
    addLog(`Generated ${generatedGroups.length} Cloudflare rule group${generatedGroups.length !== 1 ? 's' : ''}.`, 'info');
    saveToHistory(activeUrls, newResults);
  };

  const exportToCsv = () => {
    const csv = Papa.unparse(results);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'redirect-results.csv';
    link.click();
  };

  // Memoized filtered and sorted results
  const displayedResults = useMemo(() => {
    const searchLower = searchText.toLowerCase();
    return results
      .filter(r =>
        (!searchText ||
          r.originalUrl.toLowerCase().includes(searchLower) ||
          r.redirectUrl.toLowerCase().includes(searchLower) ||
          r.type.toLowerCase().includes(searchLower)
        ) &&
        (!filterType || r.type === filterType) &&
        (!filterConfidence || r.confidence === filterConfidence)
      )
      .sort((a, b) => {
        if (!sortBy) return (a.inputIndex ?? 0) - (b.inputIndex ?? 0);
        let aVal = a[sortBy] || '';
        let bVal = b[sortBy] || '';
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = (b[sortBy] || '').toLowerCase();
        }
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
  }, [results, searchText, filterType, filterConfidence, sortBy, sortDirection]);

  // Professional portal theme
  const theme = createTheme({
    palette: {
      primary: {
        main: '#1e40af',
        light: '#3b82f6',
        dark: '#1e3a8a'
      },
      secondary: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669'
      },
      success: {
        main: '#10b981'
      },
      warning: {
        main: '#f59e0b'
      },
      error: {
        main: '#ef4444'
      },
      background: {
        default: '#f9fafb',
        paper: '#ffffff'
      }
    },
    typography: {
      fontFamily: '"Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.02em'
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.01em'
      },
      h6: {
        fontWeight: 600
      }
    }
  });

  const getStatusIcon = (type) => {
    switch(type) {
      case 'Story':
      case 'Section':
      case 'Tag':
      case 'Author':
        return <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />;
      case 'Error':
        return <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />;
      case 'External Link Error':
        return <ErrorIcon sx={{ color: '#f59e0b', fontSize: 20 }} />;
      case 'Unknown':
        return <WarningIcon sx={{ color: '#f59e0b', fontSize: 20 }} />;
      default:
        return <InfoIcon sx={{ color: '#6b7280', fontSize: 20 }} />;
    }
  };

  const getConfidenceColor = (confidence) => {
    switch(confidence) {
      case 'High':
        return '#10b981';
      case 'Medium':
        return '#f59e0b';
      case 'Low':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  // Calculate statistics
  const stats = useMemo(() => ({
    total: results.length,
    matched: results.filter(r => r.type !== 'Unknown' && r.type !== 'Error').length,
    highConfidence: results.filter(r => r.confidence === 'High').length,
    errors: results.filter(r => r.type === 'Error').length,
    successRate: results.length > 0 ? Math.round((results.filter(r => r.type !== 'Unknown' && r.type !== 'Error').length / results.length) * 100) : 0
  }), [results]);

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb' }}>
          <Box sx={{ textAlign: 'center', p: 4, maxWidth: 400 }}>
            <Box component="img" src="/pathfinder-logo.svg" alt="Pathfinder" sx={{ height: 64, mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 1 }}>Sign in to Pathfinder</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>Access restricted to @quintype.com accounts</Typography>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setAuthError('Google login failed. Try again.')}
              useOneTap
              theme="outline"
              size="large"
              width="320"
            />
            {authError && (
              <Typography variant="body2" sx={{ mt: 2, color: '#ef4444', fontWeight: 500 }}>{authError}</Typography>
            )}
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Professional Header */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          mb: 2
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box component="img"
            src="/pathfinder-logo.svg"
            alt="Pathfinder logo"
            sx={{ height: 64, width: 'auto', maxWidth: 280, objectFit: 'contain' }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="small" variant="outlined" startIcon={<HistoryIcon />}
              onClick={() => setHistoryOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}
            >
              History
            </Button>
            {user.picture && (
              <Box component="img" src={user.picture} alt={user.name}
                sx={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid #e5e7eb' }}
              />
            )}
            <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500 }}>{user.name}</Typography>
            <Button size="small" variant="outlined" onClick={handleLogout}
              sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#6b7280', '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}>
              Sign out
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ pb: 6 }}>
        
        {/* Statistics Dashboard */}
        {results.length > 0 && (
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                  <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Total URLs
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e40af', mt: 1 }}>
                    {stats.total}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                  <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Matched
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981', mt: 1 }}>
                    {stats.matched}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                  <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Success Rate
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#3b82f6', mt: 1 }}>
                    {stats.successRate}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                  <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    High Confidence
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#059669', mt: 1 }}>
                    {stats.highConfidence}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
              <Card sx={{ height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                  <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Errors
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef4444', mt: 1 }}>
                    {stats.errors}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Upload Section */}
        <Card sx={{ mb: 4, boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb' }}>
          <CardContent sx={{ p: 3.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <CloudUploadIcon sx={{ color: '#1e40af', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
                Upload & Analyze URLs
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Dropzone */}
              <Grid item xs={12} md={6}>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed #1e40af',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: '#f0f9ff',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: '#e0f2fe',
                      borderColor: '#1e3a8a',
                      boxShadow: '0 4px 12px rgba(30, 64, 175, 0.1)'
                    }
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#1e40af', mb: 1.5 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                    Drag CSV/TXT files here
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    or click to select from your computer
                  </Typography>
                </Box>
              </Grid>

              {/* Manual Text Input */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Or paste URLs here (one per line)"
                  value={rawUrlText}
                  onChange={(e) => {
                    setRawUrlText(e.target.value);
                    setUrls([]);
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f9fafb',
                      '&:hover': {
                        bgcolor: '#f3f4f6'
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e5e7eb'
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={processUrls}
                disabled={loading || (urls.length === 0 && rawUrlText.trim() === '')}
                startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <TrendingUpIcon />}
                sx={{
                  fontWeight: 600,
                  py: 1.3,
                  px: 3.5,
                  color: 'white',
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(30, 64, 175, 0.4)',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #172554 100%)'
                  },
                  '&:disabled': {
                    background: '#cbd5e1',
                    color: '#94a3b8'
                  }
                }}
              >
                {loading ? 'Processing...' : 'Analyze URLs'}
              </Button>

              {loading && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => stopProcessing('Processing stopped by user.')}
                  sx={{
                    fontWeight: 600,
                    py: 1.3,
                    px: 3.5,
                    borderColor: '#ef4444',
                    color: '#b91c1c',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      bgcolor: '#fef2f2',
                      borderColor: '#dc2626',
                      color: '#991b1b'
                    }
                  }}
                >
                  Stop Processing
                </Button>
              )}
              
              {results.length > 0 && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={exportToCsv}
                  startIcon={<DownloadIcon />}
                  sx={{ fontWeight: 600, py: 1.3, px: 3.5, borderColor: '#1e40af', color: '#1e40af', textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: '#f0f9ff', borderColor: '#1e3a8a', color: '#1e3a8a' } }}
                >
                  Export Results
                </Button>
              )}
              
              {results.length > 0 && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setUrls([]);
                    setRawUrlText('');
                    setResults([]);
                    setSuggestions([]);
                    setCloudflareGroups([]);
                    setSearchText('');
                    setFilterType('');
                    setFilterConfidence('');
                  }}
                  startIcon={<RefreshIcon />}
                  sx={{ fontWeight: 600, py: 1.3, px: 3.5, borderColor: '#9ca3af', color: '#4b5563', textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: '#f3f4f6', borderColor: '#6b7280', color: '#1f2937' } }}
                >
                  Start Over
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {(processLog.length > 0 || results.length > 0 || cloudflareGroups.length > 0) && (
          <Card
            sx={{
              mb: 3,
              position: 'sticky',
              top: 92,
              zIndex: 1090,
              border: '1px solid #dbeafe',
              boxShadow: '0 2px 6px rgba(30,64,175,0.12)',
              bgcolor: '#f8fbff'
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={processLog.length === 0}
                  onClick={() => scrollToSection(processLogSectionRef)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Process Log
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={results.length === 0}
                  onClick={() => scrollToSection(resultsSectionRef)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Results
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={cloudflareGroups.length === 0}
                  onClick={() => scrollToSection(cloudflareSectionRef)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Cloudflare Redirect Rule Groups
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Process Log Panel */}
        {processLog.length > 0 && (
          <Card
            ref={processLogSectionRef}
            sx={{ mb: 4, boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid #1e3a8a', overflow: 'hidden' }}
          >
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TerminalIcon sx={{ color: '#4ade80', fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 600, fontFamily: 'monospace' }}>
                  Process Log
                </Typography>
                {loading && <CircularProgress size={12} sx={{ color: '#4ade80', ml: 1 }} />}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f87171' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#fbbf24' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4ade80' }} />
              </Box>
            </Box>
            <Box
              ref={logContainerRef}
              sx={{
                bgcolor: '#0f172a',
                p: 2,
                maxHeight: 280,
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                lineHeight: 1.8,
                borderTop: '1px solid #1e293b'
              }}
            >
              {processLog.map((entry, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                  <Typography component="span" sx={{
                    color: '#475569',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    flexShrink: 0,
                    userSelect: 'none'
                  }}>
                    {entry.timestamp}
                  </Typography>
                  <Typography component="span" sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.78rem',
                    color: entry.type === 'success' ? '#4ade80'
                      : entry.type === 'error' ? '#f87171'
                      : entry.type === 'warn' ? '#fbbf24'
                      : '#94a3b8',
                    wordBreak: 'break-all'
                  }}>
                    {entry.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        )}

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 1.5, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
            icon={<ErrorIcon />}
          >
            {error}
          </Alert>
        )}

        {/* Suggestions Section */}
        {suggestions.length > 0 && (
          <Card sx={{ mb: 4, boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', bgcolor: '#fffbeb' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <InfoIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e' }}>
                  Optimization Suggestions
                </Typography>
              </Box>
              <List>
                {suggestions.map((s, idx) => (
                  <React.Fragment key={s.pattern}>
                    <ListItem sx={{ py: 2 }}>
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600, color: '#1f2937', mb: 1 }}>
                            {s.pattern}
                            <Chip 
                              label={`${s.count} URLs`} 
                              size="small" 
                              sx={{ ml: 1.5, bgcolor: '#fcd34d', fontWeight: 600 }}
                            />
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            {s.examples.map((e, i) => (
                              <Typography key={i} variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                                {e.original} → <strong style={{ color: '#059669' }}>{e.suggestion}</strong>
                              </Typography>
                            ))}
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < suggestions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Results Section */}
        {results.length > 0 && (
          <Box ref={resultsSectionRef}>
            {/* Filters */}
            <Card sx={{ mb: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search results"
                      placeholder="Search by URL, type, or redirect..."
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#9ca3af', mr: 1 }} />
                          </InputAdornment>
                        )
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#f9fafb'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Type"
                      value={filterType}
                      onChange={e => setFilterType(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#f9fafb'
                        }
                      }}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="Story">Story</MenuItem>
                      <MenuItem value="Section">Section</MenuItem>
                      <MenuItem value="Tag">Tag</MenuItem>
                      <MenuItem value="Author">Author</MenuItem>
                      <MenuItem value="External Link Error">External Link Error</MenuItem>
                      <MenuItem value="Error">Error</MenuItem>
                      <MenuItem value="Unknown">Unknown</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Confidence"
                      value={filterConfidence}
                      onChange={e => setFilterConfidence(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#f9fafb'
                        }
                      }}
                    >
                      <MenuItem value="">All Levels</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="Low">Low</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Results Table */}
            <Card sx={{ boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>
                        Status
                      </TableCell>
                      <TableCell 
                        onClick={() => {
                          setSortBy('originalUrl');
                          setSortDirection(sortBy === 'originalUrl' && sortDirection === 'asc' ? 'desc' : 'asc');
                        }}
                        sx={{ cursor: 'pointer', fontWeight: 700, color: '#374151', py: 2, '&:hover': { bgcolor: '#e5e7eb' } }}
                      >
                        Original URL {sortBy === 'originalUrl' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell 
                        onClick={() => {
                          setSortBy('type');
                          setSortDirection(sortBy === 'type' && sortDirection === 'asc' ? 'desc' : 'asc');
                        }}
                        sx={{ cursor: 'pointer', fontWeight: 700, color: '#374151', py: 2, '&:hover': { bgcolor: '#e5e7eb' } }}
                      >
                        Type {sortBy === 'type' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell 
                        onClick={() => {
                          setSortBy('redirectUrl');
                          setSortDirection(sortBy === 'redirectUrl' && sortDirection === 'asc' ? 'desc' : 'asc');
                        }}
                        sx={{ cursor: 'pointer', fontWeight: 700, color: '#374151', py: 2, '&:hover': { bgcolor: '#e5e7eb' } }}
                      >
                        Redirect URL {sortBy === 'redirectUrl' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell 
                        onClick={() => {
                          setSortBy('confidence');
                          setSortDirection(sortBy === 'confidence' && sortDirection === 'asc' ? 'desc' : 'asc');
                        }}
                        sx={{ cursor: 'pointer', fontWeight: 700, color: '#374151', py: 2, textAlign: 'center', '&:hover': { bgcolor: '#e5e7eb' } }}
                      >
                        Confidence {sortBy === 'confidence' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedResults.map((result, index) => (
                      <TableRow 
                        key={index}
                        sx={{
                          borderBottom: '1px solid #f3f4f6',
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell sx={{ py: 2, pl: 2 }}>
                          {getStatusIcon(result.type)}
                        </TableCell>
                        <TableCell sx={{ py: 2, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1f2937', fontWeight: 500 }}>
                            {result.originalUrl}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={result.type}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              borderColor: '#d1d5db',
                              color: '#374151'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#059669', fontWeight: 500 }}>
                            {result.redirectUrl}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2, textAlign: 'center' }}>
                          <Chip 
                            label={result.confidence}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: getConfidenceColor(result.confidence),
                              color: 'white'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {displayedResults.length === 0 && results.length > 0 && (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9fafb' }}>
                  <InfoIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    No results match your filters
                  </Typography>
                </Box>
              )}
            </Card>
          </Box>
        )}

        {cloudflareGroups.length > 0 && (
          <Card
            ref={cloudflareSectionRef}
            sx={{ mb: 4, boxShadow: '0 4px 6px rgba(0,0,0,0.07)', border: '1px solid #bfdbfe', bgcolor: '#eff6ff' }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <InfoIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e3a8a' }}>
                  Cloudflare Redirect Rule Groups
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#1e40af', mb: 2 }}>
                Reusable groups only (2+ URLs). Use the Safe match expression when provided to avoid broad wildcard redirects.
              </Typography>
              <List>
                {cloudflareGroups.map((group, idx) => (
                  <React.Fragment key={`${group.groupType}-${group.sourcePattern}-${group.targetPattern}`}>
                    <ListItem sx={{ py: 2, alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1 }}>
                            <Chip label={group.groupType} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 700 }} />
                            <Chip label={`${group.count} URLs`} size="small" sx={{ bgcolor: '#bfdbfe', color: '#1d4ed8', fontWeight: 700 }} />
                            <Chip label={`AI confidence: ${group.confidence}`} size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }} />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ color: '#1f2937', mb: 0.5, fontFamily: 'monospace' }}>
                              Source pattern: {group.sourcePattern}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#065f46', mb: 1, fontFamily: 'monospace' }}>
                              Target pattern: {group.targetPattern}
                            </Typography>
                            {group.safeExpression ? (
                              <Typography variant="body2" sx={{ color: '#0c4a6e', mb: 1, fontFamily: 'monospace' }}>
                                Safe match expression: {group.safeExpression}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#b45309', mb: 1 }}>
                                Warning: broad wildcard pattern. Review manually before creating a Cloudflare rule.
                              </Typography>
                            )}
                            {group.examples.map((ex, exIdx) => (
                              <Typography key={exIdx} variant="body2" sx={{ color: '#4b5563', mb: 0.5 }}>
                                {ex.original} → {ex.redirect}
                              </Typography>
                            ))}
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < cloudflareGroups.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {results.length === 0 && urls.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CloudUploadIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6b7280', mb: 1, fontWeight: 600 }}>
              Ready to analyze URLs
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              Upload a CSV file or paste URLs to get started
            </Typography>
          </Box>
        )}

        {showScrollTop && (
          <Fab
            color="primary"
            aria-label="go to top"
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1200,
              boxShadow: '0 8px 18px rgba(30, 64, 175, 0.35)'
            }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        )}
      </Container>

      {/* History Drawer */}
      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: '#1e40af', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>Analysis History</Typography>
          </Box>
          <IconButton size="small" onClick={() => setHistoryOpen(false)}><RefreshIcon sx={{ transform: 'rotate(45deg)', fontSize: 20 }} /></IconButton>
        </Box>

        {(() => {
          const history = loadHistory();
          if (!history.length) {
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: '#9ca3af' }}>
                <HistoryIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.4 }} />
                <Typography variant="body2">No history yet. Run an analysis to save it here.</Typography>
              </Box>
            );
          }
          return (
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {history.map((entry, idx) => {
                const pct = entry.urlCount ? Math.round((entry.matchCount / entry.urlCount) * 100) : 0;
                const date = new Date(entry.date);
                return (
                  <Box key={entry.id} sx={{ px: 3, py: 2, borderBottom: '1px solid #f3f4f6', '&:hover': { bgcolor: '#f9fafb' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.75 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', mb: 0.25 }}>
                          {entry.domains.join(', ')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => deleteHistoryEntry(entry.id)} sx={{ color: '#d1d5db', '&:hover': { color: '#ef4444' } }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                      <Chip label={`${entry.urlCount} URLs`} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: '0.7rem' }} />
                      <Chip label={`${entry.matchCount} matched`} size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600, fontSize: '0.7rem' }} />
                      <Chip label={`${pct}%`} size="small" sx={{ bgcolor: pct >= 90 ? '#d1fae5' : pct >= 70 ? '#fef9c3' : '#fee2e2', color: pct >= 90 ? '#065f46' : pct >= 70 ? '#92400e' : '#991b1b', fontWeight: 700, fontSize: '0.7rem' }} />
                    </Box>
                    <Button size="small" variant="contained" onClick={() => restoreHistoryEntry(entry)}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', bgcolor: '#1e40af', '&:hover': { bgcolor: '#1e3a8a' } }}>
                      Load Results
                    </Button>
                  </Box>
                );
              })}
            </Box>
          );
        })()}
      </Drawer>

    </ThemeProvider>
  );
}

export default App;